<template>
  <div>
    <section class="search-bar">
      <div class="search-meta">
        <h2 class="section-title">{{ selectedCategory === 'All' ? 'Recipes' : selectedCategory }}</h2>
      </div>
      <input
        v-model="search"
        placeholder="Search recipes by title..."
        class="search-input"
      >
    </section>
    <main>
      <div class="recipes-grid">
        <div
          v-for="(recipe, index) in filteredRecipes"
          :key="recipe.id"
          :class="['recipe-card', `recipe-card-bg-${(index % 9) + 1}`]"
          @click="$router.push(`/recipe/${recipe.id}`)"
        >
          <h3>{{ recipe.title }}</h3>
          <p class="category">{{ recipe.category }}</p>
          <p v-if="recipe.contributor" class="contributor">By: {{ recipe.contributor }}</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchFromApi } from '@/services/api'

export default {
  name: 'HomeView',
  setup() {
    const recipes = ref([])
    const route = useRoute()
    const selectedCategory = ref('All')
    const search = ref('')
    const apiUrl = process.env.VUE_APP_API_URL

    onMounted(async () => {
      try {
        let data = []
        if (apiUrl) {
          data = await fetchFromApi('recipes')
        } else {
          const response = await fetch('/recipes/index.json')
          data = await response.json()
        }
        recipes.value = data
      } catch (error) {
        console.error('Failed to load recipes:', error)
      }
    })

    const syncCategory = () => {
      const category = route.query.category
      selectedCategory.value = typeof category === 'string' && category.length ? category : 'All'
    }

    syncCategory()

    watch(() => route.query.category, () => {
      syncCategory()
    })

    const filteredRecipes = computed(() => {
      let filtered = recipes.value
      if (selectedCategory.value !== 'All') {
        filtered = filtered.filter(r => r.category === selectedCategory.value)
      }
      if (search.value) {
        const query = search.value.toLowerCase()
        filtered = filtered.filter(r =>
          r.title.toLowerCase().includes(query)
        )
      }
      return filtered
    })

    return {
      recipes,
      selectedCategory,
      search,
      filteredRecipes
    }
  }
}
</script>

<style>
.search-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.search-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.section-title {
  margin: 0;
  font-size: 1.8rem;
}

.active-filter {
  margin: 0;
  color: var(--muted-text);
  font-size: 1rem;
}

.search-input {
  width: 100%;
  max-width: 360px;
  padding: 10px;
  font-size: 16px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text);
  border-radius: 4px;
}

.search-input::placeholder {
  color: var(--muted-text);
}

.recipes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.recipe-card {
  --card-bg-position: 0% 0%;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: #fffaf4;
  border: 1px solid #e2d6c4;
  padding: 15px;
  cursor: pointer;
  border-radius: 8px;
  transition: box-shadow 0.2s;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #2b2219;
}

.recipe-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url('~@/assets/recipe-card-spritesheet-clean.png');
  background-size: 400% 300%;
  background-position: var(--card-bg-position);
  background-repeat: no-repeat;
  opacity: 0.7;
  z-index: 0;
}

.recipe-card > * {
  position: relative;
  z-index: 1;
}

.recipe-card:hover {
  box-shadow: var(--shadow);
}

.recipe-card h3 {
  margin-top: 0;
  color: #2b2219;
}

.category {
  color: #675a4d;
  font-style: italic;
  font-size: 0.95rem;
}

.contributor {
  color: #675a4d;
  font-size: 14px;
}

.recipe-card-bg-1 { --card-bg-position: 0% 0%; }
.recipe-card-bg-2 { --card-bg-position: 33.333% 0%; }
.recipe-card-bg-3 { --card-bg-position: 66.667% 0%; }
.recipe-card-bg-4 { --card-bg-position: 100% 0%; }
.recipe-card-bg-5 { --card-bg-position: 0% 50%; }
.recipe-card-bg-6 { --card-bg-position: 33.333% 50%; }
.recipe-card-bg-7 { --card-bg-position: 66.667% 50%; }
.recipe-card-bg-8 { --card-bg-position: 100% 50%; }
.recipe-card-bg-9 { --card-bg-position: 0% 100%; }
</style>
